import type { GatewayError } from '@/utils/gatewayError'
import * as React from 'react'
import { Box, Button, Typography } from '@mui/material'
import KpiResults from '@/components/DatePickers/KpiResults'
import WeeklyConsumptionChart from '@/components/WeeklyConsumptionChart/WeeklyConsumptionChart'
import { useAppTheme } from '@/theme/ThemeContext'
import dayjs from 'dayjs'
import DateRangeFilterCard from '@/components/common/DateRangeFilterCard'
import { firstFiniteConsumption } from '@/utils/consumptionFormat'
import { isolateNumber } from '@/utils/numberDirection'
import {
  formatWeeklyDateRange,
  getLatestWeekRange,
  shiftWeekRange,
  type WeeklyDateRange,
} from '@/utils/weeklyDateRange'
import { getDefaultRange } from '@/utils/consumerReadingsDateRanges'
export type DateRange = { from: Date | null; to: Date | null }

type KPI = { label: string; value: string | number; sublabel?: string }

type Props = {
  range?: DateRange
  onRangeChange?: (r: DateRange) => void
  onSearch?: (from?: string, to?: string) => void
  kpis?: KPI[]
  disabled?: boolean
  loading?: boolean
  minValue?: number | null
  maxValue?: number | null
  onJumpMin?: () => void
  onJumpMax?: () => void
  data?: any[]
  chartData?: any[]
  chartLoading?: boolean
  chartError?: GatewayError | null
  onChartWeekChange?: (from: string, to: string) => void
  totalConsumption?: number
  avgConsumption?: number
  totalNightlyConsumption?: number
  avgNightlyConsumption?: number
}



export default function DailyFilter({
  range: rangeProp,
  onRangeChange,
  onSearch,
  disabled,
  loading,
  minValue = null,
  maxValue = null,
  onJumpMin,
  onJumpMax,
  data,
  chartData: chartRows,
  chartLoading,
  chartError,
  onChartWeekChange,
  totalConsumption,
  avgConsumption,
  totalNightlyConsumption,
  avgNightlyConsumption,
}: Props) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const colors = {
      cardBg: isDarkMode ? '#1D325A' : '#FAFBFD',
      cardBorder: isDarkMode ? '#374151' : '#E6ECF2',
      titleText: isDarkMode ? '#FFFFFF' : '#0F3E66',
      divider: isDarkMode ? '#374151' : '#E6ECF2',
      dash: isDarkMode ? '#94A3B8' : '#93A3B5',
      activeBtnBg: isDarkMode ? '#2d4a7c' : '#E3F5FF',
      minValue: isDarkMode ? '#FFFFFF' : '#003F61',
      unit: isDarkMode ? '#B0B0B0' : '#003F61',
      label: isDarkMode ? '#B0B0B0' : '#003F61',
      iconFilter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
  }

  const panelCardSx = {
    p: 2,
    borderRadius: 2,
    border: `1px solid ${colors.cardBorder}`,
    bgcolor: colors.cardBg,
  }

  const getDefaultRangeForFilter = (): DateRange => getDefaultRange(0, 30)

  const [innerRange, setInnerRange] =
    React.useState<DateRange>(getDefaultRangeForFilter())

  const isControlled = rangeProp !== undefined
  const currRange: DateRange = isControlled
    ? (rangeProp as DateRange)
    : innerRange

  const applyRange = React.useCallback(
    (next: DateRange) => {
      onRangeChange?.(next)
      if (!isControlled) setInnerRange(next)
    },
    [onRangeChange, isControlled]
  )

  const { from, to } = currRange

  const [appliedRange, setAppliedRange] = React.useState<WeeklyDateRange | null>(
    () => (from && to ? {
      from: dayjs(from).format('YYYY-MM-DD'),
      to: dayjs(to).format('YYYY-MM-DD'),
    } : null)
  )
  const [chartWeek, setChartWeek] = React.useState<WeeklyDateRange | null>(
    () => (from && to ? getLatestWeekRange(from, to) : null)
  )

  React.useEffect(() => {
    if (chartWeek) onChartWeekChange?.(chartWeek.from, chartWeek.to)
  }, [chartWeek, onChartWeekChange])

  const handleSearch = () => {
    if (onSearch && from && to && from <= to) {
      // Format dates as YYYY-MM-DD for API
      const nextRange = {
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(to).format('YYYY-MM-DD'),
      }
      onSearch(nextRange.from, nextRange.to)
      setAppliedRange(nextRange)
      setChartWeek(getLatestWeekRange(nextRange.from, nextRange.to))
    }
  }

  const [activeBtn, setActiveBtn] = React.useState<'min' | 'max' | null>(null)

  // Transform API data to chart format
  const chartData = React.useMemo(() => {
    const sourceRows = chartRows ?? data
    if (sourceRows && sourceRows.length > 0) {
      // The dedicated chart request already contains the selected week.
      const sortedData = [...sourceRows].sort((a, b) => {
        const dateA = dayjs(a.timestamp || a.date || a.day)
        const dateB = dayjs(b.timestamp || b.date || b.day)
        return dateA.valueOf() - dateB.valueOf()
      })
      const recentData = chartRows ? sortedData : sortedData.slice(-7)

      return recentData.map((item: any) => {
        // Parse date using dayjs - handle both transformed and raw API data
        const dateStr = item.timestamp || item.date || item.day
        const parsedDate = dayjs(dateStr)

        // Get consumption values - handle both transformed and raw API field names
        const dailyConsumption = firstFiniteConsumption(
          item.consumption,
          item.dailyConsumption
        ) ?? 0
        const nightConsumption = firstFiniteConsumption(
          item.nightConsumption,
          item.nightlyConsumption
        ) ?? 0

        return {
          date: parsedDate.isValid() ? parsedDate.toISOString() : dateStr,
          day: dailyConsumption, // צ. יום - Day consumption
          night: nightConsumption, // צ. לילה - Night consumption
        }
      })
    }
    return []
  }, [chartRows, data])

  const previousWeek = React.useMemo(() => {
    if (!chartWeek) return null
    if (appliedRange) {
      const shifted = shiftWeekRange(chartWeek, appliedRange, 'previous')
      if (shifted) return shifted
    }
    const prevEnd = dayjs(chartWeek.from).subtract(1, 'day')
    const prevStart = prevEnd.subtract(6, 'day')
    return {
      from: prevStart.format('YYYY-MM-DD'),
      to: prevEnd.format('YYYY-MM-DD'),
    }
  }, [chartWeek, appliedRange])

  const nextWeek = React.useMemo(() => {
    if (!chartWeek) return null
    if (appliedRange) {
      const shifted = shiftWeekRange(chartWeek, appliedRange, 'next')
      if (shifted) return shifted
    }
    const nextStart = dayjs(chartWeek.to).add(1, 'day')
    if (nextStart.isAfter(dayjs().add(1, 'day'))) return null
    const nextEnd = nextStart.add(6, 'day')
    return {
      from: nextStart.format('YYYY-MM-DD'),
      to: nextEnd.format('YYYY-MM-DD'),
    }
  }, [chartWeek, appliedRange])

  // Check if we have real data from API
  const hasApiData = data && data.length > 0 && (totalConsumption !== undefined || avgConsumption !== undefined)
  return (
    <Box
      dir="rtl"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ml: '10px',
      }}
    >
      <DateRangeFilterCard
        range={currRange}
        onRangeChange={applyRange}
        onSearch={() => handleSearch()}
        loading={loading}
        disabled={disabled}
        compact
      />

      {loading ? (
        /* Show loading skeletons while data is being fetched */
        <>
          {/* KPI Result skeleton 1 */}
          <Box sx={{ ...panelCardSx, display: 'flex', gap: 2 }}>
            {[1, 2].map((i) => (
              <Box key={`kpi1-${i}`} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    height: 20,
                    width: '60%',
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    '@keyframes shimmer': {
                      '0%': { backgroundPosition: '200% 0' },
                      '100%': { backgroundPosition: '-200% 0' },
                    },
                  }}
                />
                <Box
                  sx={{
                    height: 40,
                    width: '80%',
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    animationDelay: '0.1s',
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* KPI Result skeleton 2 */}
          <Box sx={{ ...panelCardSx, display: 'flex', gap: 2 }}>
            {[1, 2].map((i) => (
              <Box key={`kpi2-${i}`} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    height: 20,
                    width: '60%',
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    animationDelay: '0.2s',
                  }}
                />
                <Box
                  sx={{
                    height: 40,
                    width: '80%',
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    animationDelay: '0.3s',
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Chart skeleton */}
          <Box sx={{ ...panelCardSx, display: 'flex', flexDirection: 'column', gap: 1, height: 200 }}>
            <Box
              sx={{
                height: 24,
                width: '50%',
                borderRadius: 1,
                background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
              }}
            />
            <Box
              sx={{
                flex: 1,
                borderRadius: 1,
                background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
                animationDelay: '0.4s',
              }}
            />
          </Box>

          {/* MIN/MAX buttons skeleton */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {['MIN', 'MAX'].map((label, idx) => (
              <Box
                key={label}
                sx={{
                  ...panelCardSx,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  height: 80,
                }}
              >
                <Box
                  sx={{
                    height: 40,
                    width: '70%',
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    animationDelay: `${idx * 0.1}s`,
                  }}
                />
                <Box
                  sx={{
                    height: 20,
                    width: '30%',
                    borderRadius: 1,
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    animationDelay: `${(idx + 1) * 0.1}s`,
                  }}
                />
              </Box>
            ))}
          </Box>
        </>
      ) : hasApiData && (
        <>
          <KpiResults
            hourlyAvgM3={totalConsumption ?? 0}
            totalM3={totalNightlyConsumption ?? 0}
            firstTitle="סך צריכה"
            secondTitle="סך צריכה"
            firstDateRangeText="צ. יום"
            secondDateRangeText="צ. לילה"
          />
          <KpiResults
            hourlyAvgM3={avgConsumption ?? 0}
            totalM3={avgNightlyConsumption ?? 0}
            firstTitle="ממוצע צריכה"
            secondTitle="ממוצע צריכה"
            firstDateRangeText="צ. יום"
            secondDateRangeText="צ. לילה"
          />
          <WeeklyConsumptionChart
            data={chartData}
            loading={chartLoading}
            error={chartError}
            onRetry={() => chartWeek && onChartWeekChange?.(chartWeek.from, chartWeek.to)}
            title="גרף צריכה יומית בפריסה שבועית"
            dateFormatter={d => {
              const day = String(d.getDate()).padStart(2, '0')
              const month = String(d.getMonth() + 1).padStart(2, '0')
              return `${day}/${month}`
            }}
            seriesNames={{ day: 'צ. יום', night: 'צ. לילה' }}
            height={150}
            navigation={chartWeek ? {
              label: formatWeeklyDateRange(chartWeek),
              onPrevious: () => previousWeek && setChartWeek(previousWeek),
              onNext: () => nextWeek && setChartWeek(nextWeek),
              previousDisabled: !previousWeek,
              nextDisabled: !nextWeek,
            } : undefined}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'row',
            }}
          >
            <Button
              fullWidth
              onClick={() => {
                setActiveBtn('min')
                onJumpMin?.()
              }}
              sx={{
                ...panelCardSx,
                p: { xs: 2, lg: 1.25, xl: 1.5 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: activeBtn === 'min' ? colors.activeBtnBg : undefined,
                '&:active': {
                  backgroundColor: `${colors.activeBtnBg} !important`,
                  boxShadow: 'none',
                },
              }}
              disabled={!onJumpMin || minValue == null}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.minValue,
                    fontWeight: 'bold',
                    fontSize: { xs: '29.84px', lg: '20px', xl: '24px' },
                    fontFamily: 'Rubik, sans-serif',
                  }}
                >
                  {minValue == null ? '—' : isolateNumber(minValue)}
                </Typography>
                <Typography sx={{ color: colors.unit,fontFamily: 'Rubik, sans-serif', }}>קוב</Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ color: colors.label, fontWeight: 'bold' ,fontFamily: 'Rubik, sans-serif',}}
              >
                MIN
              </Typography>
            </Button>

            <Button
              fullWidth
              onClick={() => {
                setActiveBtn('max')
                onJumpMax?.()
              }}
              sx={{
                ...panelCardSx,
                p: { xs: 2, lg: 1.25, xl: 1.5 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: activeBtn === 'max' ? colors.activeBtnBg : undefined,
                '&:active': {
                  backgroundColor: `${colors.activeBtnBg} !important`,
                  boxShadow: 'none',
                },
              }}
              disabled={!onJumpMax || maxValue == null}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.minValue,
                    fontWeight: 'bold',
                    fontSize: { xs: '29.84px', lg: '20px', xl: '24px' },
                    fontFamily: 'Rubik, sans-serif',
                  }}
                >
                  {maxValue == null ? '—' : isolateNumber(maxValue)}
                </Typography>
                <Typography sx={{ color: colors.unit,fontFamily: 'Rubik, sans-serif', }}>קוב</Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: colors.label, fontWeight: 'bold',fontFamily: 'Rubik, sans-serif', }}
              >
                MAX
              </Typography>
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}
